import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const MS_DAY = 86_400_000;

function lastTwoWeeks(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(Date.now() - 13 * MS_DAY);
  return { from: from.toISOString(), to: to.toISOString() };
}

interface ActivityUIState {
  statusFilter: string; // '' = all
  typeFilter: '' | 'Labour' | 'Variation';
  siteIdsFilter: number[];
  fromDate: string;
  toDate: string;
  setStatusFilter: (status: string) => void;
  setTypeFilter: (type: '' | 'Labour' | 'Variation') => void;
  toggleSiteId: (id: number) => void;
  setSiteIds: (ids: number[]) => void;
  setDateRange: (from: string, to: string) => void;
  clearFilters: () => void;
}

const initialRange = lastTwoWeeks();

export const useActivityUIStore = create<ActivityUIState>()(
  persist(
    set => ({
      statusFilter: '',
      typeFilter: '',
      siteIdsFilter: [],
      fromDate: initialRange.from,
      toDate: initialRange.to,
      setStatusFilter: status => set({ statusFilter: status }),
      setTypeFilter: type => set({ typeFilter: type }),
      toggleSiteId: id =>
        set(state => ({
          siteIdsFilter: state.siteIdsFilter.includes(id)
            ? state.siteIdsFilter.filter(x => x !== id)
            : [...state.siteIdsFilter, id],
        })),
      setSiteIds: ids => set({ siteIdsFilter: ids }),
      setDateRange: (from, to) => set({ fromDate: from, toDate: to }),
      clearFilters: () =>
        set({ statusFilter: '', typeFilter: '', siteIdsFilter: [] }),
    }),
    {
      name: 'activity-ui',
      storage: createJSONStorage(() => localStorage),
      // Persist the discrete filter selections so they survive navigation and
      // reloads. The date window is deliberately excluded — it resets to the
      // last two weeks each session rather than restoring a stale absolute range.
      partialize: state => ({
        statusFilter: state.statusFilter,
        typeFilter: state.typeFilter,
        siteIdsFilter: state.siteIdsFilter,
      }),
    }
  )
);
